import { createReducer, on } from '@ngrx/store';
import * as RidesSalesActions from './rides-sales.actions';
import { RidesSale, RidesTotalMoney } from './rides-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface RidesSalesState {
    ridesSales: RidesSale[];
    ridesSale: RidesSale | null;
    ridesTotalMoney: RidesTotalMoney | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialRidesSalesState: RidesSalesState = {
    ridesSales: [],
    ridesSale: null,
    ridesTotalMoney: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All RidesSales Reducer
export const ridesSalesReducer = createReducer(
    initialRidesSalesState,

    // On Load Rides Sales
    on(RidesSalesActions.loadRidesSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.loadRidesSalesSuccess, (state, { ridesSales }) => ({
        ...state,
        isLoading: false,
        ridesSales: ridesSales.rides,
        ridesTotalMoney: ridesSales.total_money,
    })),
    on(RidesSalesActions.loadRidesSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(RidesSalesActions.clearRidesSales, (state) => ({
        ...state,
        ridesSales: [],
        ridesSale: null,
        ridesTotalMoney: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single Rides Sale
    on(RidesSalesActions.loadRidesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.loadRidesSaleSuccess, (state, { ridesSale }) => ({
        ...state,
        isLoading: false,
        ridesSale: ridesSale,
    })),
    on(RidesSalesActions.loadRidesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(RidesSalesActions.createRidesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.createRidesSaleSuccess, (state, { ridesSale }) => ({
        ...state,
        isLoading: false,
        ridesSales: [...(state.ridesSales || []), ridesSale],
    })),
    on(RidesSalesActions.createRidesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(RidesSalesActions.updateRidesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.updateRidesSaleSuccess, (state, { ridesSale }) => ({
        ...state,
        isLoading: false,
        ridesSales: [
            ...state.ridesSales.map((r) =>
                r.id === ridesSale.id ? ridesSale : r
            ),
        ],
    })),
    on(RidesSalesActions.updateRidesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete
    on(RidesSalesActions.deleteRidesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.deleteRidesSaleSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        ridesSales: [
            ...state.ridesSales.filter((r) => r.midway_ride_id !== id),
        ],
    })),
    on(RidesSalesActions.deleteRidesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Sales Rep
    on(RidesSalesActions.updateRidesSaleSalesRep, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RidesSalesActions.updateRidesSaleSalesRepSuccess,
        (state, { ridesSale }) => ({
            ...state,
            isLoading: false,
            ridesSales: state.ridesSales.map((r) =>
                r.midway_ride_id === ridesSale.midway_ride_id ? ridesSale : r
            ),
        })
    ),
    on(
        RidesSalesActions.updateRidesSaleSalesRepFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Delete Sales Rep
    on(RidesSalesActions.deleteRidesSaleSalesRep, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RidesSalesActions.deleteRidesSaleSalesRepSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        ridesSales: state.ridesSales.map((r) =>
            r.children?.some((child) => child.mrc_id === id)
                ? { ...r, children: r.children.filter((c) => c.mrc_id !== id) }
                : r
        ),
    })),
    on(
        RidesSalesActions.deleteRidesSaleSalesRepFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
